import { isCitationReference } from "#features/citations/citedContent.ts";

interface MarkdownNode { type: string; value?: string; url?: string; children?: MarkdownNode[]; data?: object }

/** The class of the span that holds a run of citation pills and the word before it. */
export const CITATION_RUN_CLASS = "wai-citation-nowrap";

const textOf = (node: MarkdownNode): string => node.value ?? (node.children ?? []).map(textOf).join("");
const isBlank = (node?: MarkdownNode) => node?.type === "text" && /^[\s\u2060]*$/.test(node.value ?? "");

// A citation marker: `[n](#...)` as formatCitedContent writes it, or a link whose destination is the
// reference. A numeric link to a real page stays an ordinary link.
const isCitation = (node?: MarkdownNode) => node?.type === "link" && (isCitationReference(node.url) || (!!node.url?.startsWith("#")
    && node.children?.length === 1 && node.children[0].type === "text" && isCitationReference(node.children[0].value)));

// A one-word link or code span stays with the pill; an autolinked URL stays free to wrap.
const isOneWord = (node: MarkdownNode) => ["link", "inlineCode"].includes(node.type)
    && !/\s/.test(textOf(node)) && !node.url?.endsWith(textOf(node));

const groupCitations = (node: MarkdownNode) => {
    const children = node.children ?? [];
    const grouped: MarkdownNode[] = [];

    for (let index = 0; index < children.length;) {
        const run: MarkdownNode[] = [];

        // A run is citation links with only blank text between them.
        for (; isCitation(children[index]) || (run.length && isBlank(children[index]) && isCitation(children[index + 1])); index++) {
            run.push(...(isCitation(children[index]) ? [children[index]] : []));
        }

        if (!run.length) {
            groupCitations(children[index]);
            grouped.push(children[index++]);
            continue;
        }

        // The spaces before the run go, and the punctuation after it moves onto the word before it.
        while (isBlank(grouped.at(-1))) {
            grouped.pop();
        }
        const before = grouped.at(-1) ?? { type: "" };
        const next = children[index] ?? { type: "" };
        const punctuation = (grouped.length > 0 && next.type === "text" && /^[.,;:!?]+/.exec(next.value!)?.[0]) || "";
        const word = before.type === "text" ? /\S+\s*$/.exec(before.value!)![0] : "";
        const tail = !word && isOneWord(before) ? [before] : [];
        const head = word || punctuation ? [{ type: "text", value: word.trimEnd() + punctuation }] : [];

        next.value = next.value?.slice(punctuation.length);
        before.value = before.value?.slice(0, before.value.length - word.length);
        grouped.splice(grouped.length - tail.length, tail.length, { type: "citationGroup", children: [...tail, ...head, ...run],
            data: { hName: "span", hProperties: { className: [CITATION_RUN_CLASS] } } });
    }

    if (node.children) {
        node.children = grouped.filter(child => child.type !== "text" || child.value);
    }
};

/** Keeps each run of citation pills, after the punctuation, on the line of the word before it: one
 * `nowrap` span holds all three. A word joiner cannot: Chrome still breaks before an inline-flex box. */
export const remarkCitationNowrap = () => (tree: { type: string }) => groupCitations(tree as MarkdownNode);
